// Image Magnifier Functionality - Optimized
class ProductImageMagnifier {
  constructor(section) {
    this.section = section;
    this.mediaList = section.querySelector('.js-media-list');
    this.mediaListWrapper = section.querySelector('.product__media-list-wrapper');
    this.mediaItems = section.querySelectorAll('.product__media-item');
    this.isDesktop = window.matchMedia('(min-width: 990px)').matches;
    
    if (!this.mediaList || !this.mediaItems.length) {
      return;
    }

    this.zoomLevel = 2;
    this.lensSize = 200;
    this.animationFrameId = null;
    this.pendingUpdate = false;

    this.init();
    this.attachEventListeners();
  }

  init() {
    // Create magnified container for desktop
    if (this.isDesktop) {
      this.createMagnifiedContainer();
    }

    // Setup each media item
    this.mediaItems.forEach((item) => {
      const img = item.querySelector('img');
      if (!img) return;

      // Skip if image is not loaded yet
      if (!img.complete) {
        img.addEventListener('load', () => {
          this.setupImageMagnifier(item, img);
        });
      } else {
        this.setupImageMagnifier(item, img);
      }
    });

    // Handle media changes (for slider)
    if (this.mediaList.swiper) {
      this.mediaList.swiper.on('slideChange', () => {
        this.updateMagnifier();
      });
    }
  }

  createMagnifiedContainer() {
    if (!this.mediaListWrapper || this.mediaListWrapper.querySelector('.product__media-magnified-container')) {
      return;
    }

    const magnifiedContainer = document.createElement('div');
    magnifiedContainer.className = 'product__media-magnified-container';
    
    const img = document.createElement('img');
    img.id = 'magnified-image';
    
    magnifiedContainer.appendChild(img);
    this.mediaListWrapper.appendChild(magnifiedContainer);
    
    this.magnifiedContainer = magnifiedContainer;
    this.magnifiedImage = img;
  }

  setupImageMagnifier(item, img) {
    const wrapper = item.querySelector('.product__media-img');
    if (!wrapper) return;

    // Create lens element
    let lens = item.querySelector('.product__media-magnifier-lens');
    if (!lens) {
      lens = document.createElement('div');
      lens.className = 'product__media-magnifier-lens';
      wrapper.appendChild(lens);
    }

    // Get image dimensions
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    
    if (imgWidth === 0 || imgHeight === 0) {
      return;
    }

    item.dataset.originalWidth = imgWidth;
    item.dataset.originalHeight = imgHeight;

    // Add magnifier wrapper class
    if (!wrapper.classList.contains('product__media-magnifier-wrapper')) {
      wrapper.classList.add('product__media-magnifier-wrapper');
    }

    // Setup events on the wrapper
    this.setupMagnifierEvents(wrapper, item, lens, img);
  }

  setupMagnifierEvents(wrapper, item, lens, img) {
    let isActive = false;
    let wrapperRect = null;

    const updateMagnifierPosition = (x, y) => {
      if (!isActive || !wrapperRect) return;

      // Use requestAnimationFrame to batch DOM updates
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      this.animationFrameId = requestAnimationFrame(() => {
        const relX = x - wrapperRect.left;
        const relY = y - wrapperRect.top;

        // Position lens
        let lensX = relX - this.lensSize / 2;
        let lensY = relY - this.lensSize / 2;

        // Constrain lens within image bounds
        lensX = Math.max(0, Math.min(lensX, wrapperRect.width - this.lensSize));
        lensY = Math.max(0, Math.min(lensY, wrapperRect.height - this.lensSize));

        // Update lens position
        lens.style.left = lensX + 'px';
        lens.style.top = lensY + 'px';

        // Update magnified container on right side
        if (this.magnifiedContainer && this.magnifiedImage && isActive) {
          const lensRatioX = lensX / wrapperRect.width;
          const lensRatioY = lensY / wrapperRect.height;

          // Calculate transform for zoomed image
          const offsetX = lensRatioX * (this.magnifiedImage.naturalWidth || 0) - (this.magnifiedImage.naturalWidth || 0) / this.zoomLevel / 2+20;
          const offsetY = lensRatioY * (this.magnifiedImage.naturalHeight || 0) - (this.magnifiedImage.naturalHeight || 0) / this.zoomLevel / 2+20;

          this.magnifiedImage.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(${this.zoomLevel})`;
        }

        this.animationFrameId = null;
      });
    };

    wrapper.addEventListener('mouseenter', (e) => {
      if (!this.isDesktop) return;

      isActive = true;
      wrapperRect = wrapper.getBoundingClientRect();
      
      lens.classList.add('active');
      if (this.magnifiedContainer) {
        this.magnifiedContainer.classList.add('active');
        this.magnifiedImage.src = img.src;
      }
      this.section.closest('.product__media')?.classList.add('magnifier-active');
    });

    wrapper.addEventListener('mousemove', (e) => {
      if (!isActive) return;
      
      updateMagnifierPosition(e.clientX, e.clientY);
    });

    wrapper.addEventListener('mouseleave', (e) => {
      if (!this.isDesktop) return;

      isActive = false;
      
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      lens.classList.remove('active');
      if (this.magnifiedContainer) {
        this.magnifiedContainer.classList.remove('active');
      }
      this.section.closest('.product__media')?.classList.remove('magnifier-active');
    });
  }

  updateMagnifier() {
    // Update magnifier when slider changes
    const activeItem = this.mediaList.querySelector('.swiper-slide-active .product__media-item') || 
                       this.mediaList.querySelector('.product__media-item');
    
    if (activeItem && this.magnifiedImage) {
      const img = activeItem.querySelector('img');
      if (img) {
        this.magnifiedImage.src = img.src;
      }
    }
  }

  attachEventListeners() {
    // Handle window resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.matchMedia('(min-width: 990px)').matches;
        
        if (wasDesktop !== this.isDesktop) {
          if (this.isDesktop && !this.magnifiedContainer) {
            this.createMagnifiedContainer();
          }
        }
      }, 250);
    });
  }
}

// Initialize when document is ready
document.addEventListener('DOMContentLoaded', function() {
  const productSection = document.querySelector('[data-section*="product-media"]')?.closest('.product__media');
  if (productSection) {
    const parentSection = productSection.closest('[data-section]');
    if (parentSection) {
      new ProductImageMagnifier(parentSection);
    }
  }
});

// Reinitialize on Shopify section reload (for theme editor)
document.addEventListener('shopify:section:load', function(e) {
  const section = e.detail.target;
  if (section && section.classList.contains('shopify-section')) {
    new ProductImageMagnifier(section);
  }
});
